import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ControllerMode, ScheduleDoc, ScheduleEntry } from "@/data/types";
import { MODE_CATALOG, MODE_TONE } from "@/data/modeCatalog";
import { cn } from "@/lib/cn";
import {
  DAYS_SHORT,
  MINUTES_IN_DAY,
  blocksForDay,
  fmtHm,
  projectInTz,
  snapMin,
} from "./scheduleLib";

/**
 * 7-day weekly grid. The single rich-interaction surface of the schedule
 * page:
 *
 *   * Click an entry block → opens the EntryEditor on that entry.
 *   * Drag a block body horizontally → shifts the entry start+end together
 *     (snapped to 15-min steps; clamped to [00:00, 24:00]).
 *   * Drag a block edge handle → resizes start or end (same snap rules;
 *     disabled for wraps-midnight entries — see EntryBlock).
 *   * Drag a block vertically across day rows → move it to a different
 *     day (single-day entries) or add the targeted day to the
 *     `days_of_week` array (multi-day entries → keeps existing days).
 *   * Click an empty (default) area → adds an entry at that timeslot.
 *
 * Click vs. drag disambiguation: pressing a pointer (mouse, touch, pen)
 * on an entry sets a "pending drag" with the origin position. We only
 * enter actual drag mode after the pointer moves more than 4 px in any
 * direction. A plain click/tap (no movement before pointerup) flushes
 * the pending drag and fires the editor — this matches the gesture the
 * operator expects and makes the interaction robust to synthetic events
 * (preview tooling, screen readers, touch).
 *
 * Touch support (XTRA-07): drag/move/resize uses POINTER events
 * (onPointerDown + window pointermove/pointerup/pointercancel) with
 * setPointerCapture on the pressed element and `touch-none` on the
 * draggable blocks, so tablet operators can drag too. The drag math is
 * identical to the original mouse implementation.
 *
 * "Now" indicator: vertical line on today's row, positioned according to
 * the schedule document's own `timezone` field (NOT the operator's
 * browser locale). This matches what the controller-service actually
 * sees, which is the point of trust for the operator.
 */

const ROW_HEIGHT = 64;
const LEFT_GUTTER = 52;
const SNAP_MIN = 15;
const DRAG_THRESHOLD_PX = 4;

type DragKind = "move" | "resize-start" | "resize-end";

interface PendingDrag {
  kind: DragKind;
  entryId: string;
  /** Pointer that started the press — moves/ups from other pointers
   *  (a second finger) are ignored. For a mouse this is constant. */
  pointerId: number;
  /** Pixel x/y at pointerdown. */
  originPxX: number;
  originPxY: number;
  /** Entry start/end at pointerdown. */
  originStart: number;
  originEnd: number;
  originDay: number;
  /** Width of the track containing the entry at pointerdown. */
  trackWidthPx: number;
}

export const CalendarGrid = memo(function CalendarGrid({
  doc,
  editingId,
  onPick,
  onPatch,
  onAddAt,
  readOnly = false,
}: {
  doc: ScheduleDoc;
  editingId: string | null;
  onPick: (entryId: string) => void;
  onPatch: (entryId: string, mut: (e: ScheduleEntry) => ScheduleEntry) => void;
  onAddAt: (day: number, startMin: number) => void;
  readOnly?: boolean;
}) {
  const tz = doc.timezone || "Europe/Rome";
  const now = useNowInTimezone(tz);
  const trackRefs = useRef<(HTMLDivElement | null)[]>([]);
  const pendingRef = useRef<PendingDrag | null>(null);
  // True once we've crossed the drag threshold — used to suppress the
  // entry's onClick handler so we don't open the editor after a drag.
  const isDraggingRef = useRef(false);

  // Memoize blocks per day. The blocksForDay sweep is O(E log E) per
  // day so this is cheap, but memoising on the entries array + default
  // mode (not the doc reference) avoids redundant recomputes when only
  // the meta block changed.
  const todayYmd = useMemo(() => projectInTz(tz, Date.now()).ymd, [tz]);
  const blocksByDay = useMemo(
    () => Array.from({ length: 7 }, (_, d) => blocksForDay(d, doc, todayYmd)),
    [doc.entries, doc.default.mode, todayYmd],
  );

  // Latest patch handler accessed via ref so the window-bound listeners
  // never go stale across renders.
  const onPatchRef = useRef(onPatch);
  useEffect(() => { onPatchRef.current = onPatch; }, [onPatch]);

  const finishDrag = useCallback(() => {
    pendingRef.current = null;
    window.removeEventListener("pointermove", onWinMove);
    window.removeEventListener("pointerup", onWinUp);
    window.removeEventListener("pointercancel", onWinUp);
    // The trailing `click` fires AFTER pointerup, so reset the
    // drag-suppression flag a task later — the entry/track onClick
    // handlers must still see it to not fire after a real drag.
    window.setTimeout(() => { isDraggingRef.current = false; }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onWinMove = useCallback((ev: PointerEvent) => {
    const p = pendingRef.current;
    if (!p || ev.pointerId !== p.pointerId) return;
    const dxPx = ev.clientX - p.originPxX;
    const dyPx = ev.clientY - p.originPxY;
    if (!isDraggingRef.current && Math.abs(dxPx) < DRAG_THRESHOLD_PX && Math.abs(dyPx) < DRAG_THRESHOLD_PX) {
      return;
    }
    isDraggingRef.current = true;
    const dxMin = (dxPx / p.trackWidthPx) * MINUTES_IN_DAY;
    const dyDays = Math.round(dyPx / ROW_HEIGHT);
    let nextStart = p.originStart;
    let nextEnd = p.originEnd;
    if (p.kind === "move") {
      const span = ((p.originEnd - p.originStart) + MINUTES_IN_DAY) % MINUTES_IN_DAY || MINUTES_IN_DAY;
      if (span === MINUTES_IN_DAY) {
        // Full-day block (00:00 → 24:00): any horizontal nudge would
        // collapse it to start === end — which entryCovers treats as
        // empty, so the block vanished mid-drag. Pin the window; the
        // vertical day-move below still applies.
        nextStart = p.originStart;
        nextEnd = p.originEnd;
      } else {
        const shiftedStart = snapMin(p.originStart + dxMin, SNAP_MIN);
        nextStart = Math.max(0, Math.min(MINUTES_IN_DAY - 1, shiftedStart));
        const candidateEnd = nextStart + span;
        nextEnd = candidateEnd > MINUTES_IN_DAY ? candidateEnd - MINUTES_IN_DAY : candidateEnd;
      }
    } else if (p.kind === "resize-start") {
      const candidate = snapMin(p.originStart + dxMin, SNAP_MIN);
      nextStart = Math.max(0, Math.min(MINUTES_IN_DAY - SNAP_MIN, candidate));
      if (nextStart >= p.originEnd) nextStart = Math.max(0, p.originEnd - SNAP_MIN);
    } else {
      const candidate = snapMin(p.originEnd + dxMin, SNAP_MIN);
      nextEnd = Math.max(SNAP_MIN, Math.min(MINUTES_IN_DAY, candidate));
      if (nextEnd <= p.originStart) nextEnd = Math.min(MINUTES_IN_DAY, p.originStart + SNAP_MIN);
    }
    onPatchRef.current(p.entryId, (cur) => {
      const next: ScheduleEntry = { ...cur, start_min: nextStart, end_min: nextEnd };
      if (p.kind === "move" && dyDays !== 0) {
        const targetDay = Math.max(0, Math.min(6, p.originDay + dyDays));
        if (cur.days_of_week.length === 1) {
          next.days_of_week = [targetDay];
        } else if (!cur.days_of_week.includes(targetDay)) {
          next.days_of_week = [...cur.days_of_week, targetDay].sort();
        }
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onWinUp = useCallback((ev: PointerEvent) => {
    const p = pendingRef.current;
    if (p && ev.pointerId !== p.pointerId) return;
    finishDrag();
  }, [finishDrag]);

  // Clean up any leaked listeners on unmount.
  useEffect(() => () => finishDrag(), [finishDrag]);

  const beginPress = (
    kind: DragKind,
    entryId: string,
    ev: React.PointerEvent,
    dayIdx: number,
  ) => {
    if (readOnly) return;
    // Second finger while a drag is live — ignore (mouse is always primary).
    if (!ev.isPrimary) return;
    const entry = doc.entries.find((e) => e.id === entryId);
    const rect = trackRefs.current[dayIdx]?.getBoundingClientRect();
    if (!entry || !rect) return;
    // Wraps-midnight entries (end < start) have their resize handles
    // hidden in EntryBlock; guard here too so no other press path can
    // start a resize whose clamps assume start < end (they'd collapse
    // the entry to a 15-min stub on the first move).
    if (kind !== "move" && entry.end_min < entry.start_min) return;
    // Keep receiving move/up even when the pointer leaves the element.
    // NOT preventDefault: the compatibility `click` after a motionless
    // tap is what opens the editor on touch.
    try {
      (ev.currentTarget as Element).setPointerCapture(ev.pointerId);
    } catch {
      /* pointer already inactive — capture is best-effort */
    }
    pendingRef.current = {
      kind,
      entryId,
      pointerId: ev.pointerId,
      originPxX: ev.clientX,
      originPxY: ev.clientY,
      originStart: entry.start_min,
      originEnd: entry.end_min,
      originDay: dayIdx,
      trackWidthPx: rect.width,
    };
    isDraggingRef.current = false;
    window.addEventListener("pointermove", onWinMove);
    window.addEventListener("pointerup", onWinUp);
    window.addEventListener("pointercancel", onWinUp);
  };

  // ─── Render ──────────────────────────────────────────────────────────
  return (
    <div className="overflow-x-auto select-none">
      <div className="min-w-[760px]">
        {/* Hour ruler */}
        <div className="flex">
          <div className="shrink-0" style={{ width: LEFT_GUTTER }} />
          <div className="flex-1">
            <div className="grid" style={{ gridTemplateColumns: "repeat(24, 1fr)" }}>
              {Array.from({ length: 24 }, (_, h) => (
                <div
                  key={h}
                  className="border-l border-edge text-[10px] text-text-faint pl-1 py-1"
                >
                  {String(h).padStart(2, "0")}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Day rows */}
        {DAYS_SHORT.map((label, dayIdx) => {
          const isToday = now?.weekday === dayIdx;
          const blocks = blocksByDay[dayIdx];
          return (
            <div
              key={dayIdx}
              className={cn(
                "flex border-t border-edge/40",
                isToday && "bg-accent/[0.04]",
              )}
              style={{ height: ROW_HEIGHT }}
            >
              <div
                className={cn(
                  "shrink-0 px-2 py-2 text-xs font-mono",
                  isToday ? "text-accent font-semibold" : "text-text-muted",
                )}
                style={{ width: LEFT_GUTTER }}
              >
                {label}
                {isToday ? (
                  <div className="text-[9px] text-accent/80 leading-tight">today</div>
                ) : null}
              </div>
              <div
                ref={(el) => (trackRefs.current[dayIdx] = el)}
                className="relative flex-1 cursor-crosshair"
                onClick={(ev) => {
                  if (readOnly) return;
                  // Suppress add-entry on the trailing click of a drag —
                  // a cross-row drag can release over empty track.
                  if (isDraggingRef.current) return;
                  // Suppress add-entry when the click landed inside an
                  // entry block — the block's own onClick wins.
                  if ((ev.target as HTMLElement).closest("[data-entry-block]")) return;
                  const rect = ev.currentTarget.getBoundingClientRect();
                  const xFrac = (ev.clientX - rect.left) / rect.width;
                  const startMin = snapMin(xFrac * MINUTES_IN_DAY, SNAP_MIN);
                  onAddAt(dayIdx, startMin);
                }}
              >
                {/* hour grid lines */}
                {Array.from({ length: 24 }, (_, h) => (
                  <div
                    key={h}
                    className="absolute top-0 bottom-0 border-l border-edge/30"
                    style={{ left: `${(h / 24) * 100}%` }}
                  />
                ))}
                {/* entry blocks */}
                {blocks.map((b, i) => {
                  if (!b.entry) return null;
                  const left = (b.startMin / MINUTES_IN_DAY) * 100;
                  const width = ((b.endMin - b.startMin) / MINUTES_IN_DAY) * 100;
                  return (
                    <EntryBlock
                      key={`${b.entry.id}-${i}`}
                      entry={b.entry}
                      leftPct={left}
                      widthPct={width}
                      isEditing={editingId === b.entry.id}
                      readOnly={readOnly}
                      onPress={(kind, ev) => beginPress(kind, b.entry!.id, ev, dayIdx)}
                      onClick={() => {
                        // Suppress the click if we actually dragged.
                        if (isDraggingRef.current) return;
                        onPick(b.entry!.id);
                      }}
                    />
                  );
                })}
                {/* "now" line — only on today's row */}
                {isToday && now ? (
                  <div
                    className="absolute top-0 bottom-0 z-10 pointer-events-none"
                    style={{
                      left: `calc(${(now.minute / MINUTES_IN_DAY) * 100}% - 1px)`,
                    }}
                  >
                    <div className="w-0.5 h-full bg-accent" style={{ boxShadow: "0 0 6px color-mix(in srgb, var(--ems-brand) 60%, transparent)" }} />
                    <div className="absolute -top-1 -left-1 h-2.5 w-2.5 rounded-full bg-accent ring-2 ring-bg" />
                    <div className="absolute -top-3 left-2 rounded bg-bg/80 px-1 text-[9px] font-mono text-accent">
                      {now.hhmm}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="mt-3 flex flex-wrap items-center gap-2 px-1 text-[10px]">
        <span className="text-text-faint">Default:</span>
        <span className={cn("rounded border px-2 py-0.5", MODE_TONE[doc.default.mode])}>
          {MODE_CATALOG[doc.default.mode].label}
        </span>
        <span className="ml-3 text-text-faint">Modes:</span>
        {(Object.keys(MODE_CATALOG) as ControllerMode[]).map((m) => (
          <span key={m} className={cn("rounded border px-2 py-0.5", MODE_TONE[m])}>
            {MODE_CATALOG[m].label}
          </span>
        ))}
      </div>
    </div>
  );
});

function EntryBlock({
  entry,
  leftPct,
  widthPct,
  isEditing,
  readOnly,
  onPress,
  onClick,
}: {
  entry: ScheduleEntry;
  leftPct: number;
  widthPct: number;
  isEditing: boolean;
  readOnly: boolean;
  onPress: (kind: DragKind, ev: React.PointerEvent) => void;
  onClick: () => void;
}) {
  const tone = MODE_TONE[entry.mode];
  const label = entry.name || entry.id;
  // Wraps-midnight entries (end < start) render as TWO segments per day
  // (blocksForDay splits them at 00:00), so an edge handle's on-screen
  // position doesn't correspond to the entry's real start/end — and the
  // resize clamps assume start < end, instantly collapsing the window.
  // Simpler-safe option: disable edge-resize for these and point the
  // operator at the editor (body-drag move still works — its span math
  // handles the wrap).
  const wraps = entry.end_min < entry.start_min;
  const resizable = !readOnly && !wraps;
  return (
    <div
      data-entry-block
      className={cn(
        "group absolute top-1 bottom-1 rounded border text-[10px] overflow-hidden",
        tone,
        isEditing && "ring-2 ring-accent",
        !entry.enabled && "opacity-40",
        // touch-none: the browser must not turn a block-drag into a
        // scroll — covers the edge handles too (effective touch-action
        // intersects with ancestors).
        !readOnly && "cursor-grab active:cursor-grabbing touch-none",
      )}
      style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
      onPointerDown={(ev) => {
        if ((ev.target as HTMLElement).dataset.edge) return;
        onPress("move", ev);
      }}
      onClick={(ev) => {
        ev.stopPropagation();
        onClick();
      }}
      title={`${entry.id}: ${MODE_CATALOG[entry.mode].label}\n${fmtHm(entry.start_min)} → ${fmtHm(entry.end_min)}\npriority ${entry.priority}${entry.date_start ? `\nfrom ${entry.date_start}` : ""}${entry.date_end ? ` to ${entry.date_end}` : ""}${wraps && !readOnly ? "\ncrosses midnight — drag to move; resize start/end in the editor" : ""}`}
    >
      {resizable && (
        <span
          data-edge="start"
          onPointerDown={(ev) => {
            ev.stopPropagation();
            onPress("resize-start", ev);
          }}
          className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize bg-current/0 hover:bg-current/30"
          aria-label="Resize start"
        />
      )}
      <div className="px-1 py-0.5 pointer-events-none">
        <div className="truncate font-medium">{label}</div>
        <div className="truncate text-[9px] opacity-70">
          {fmtHm(entry.start_min)}–{fmtHm(entry.end_min)} · pri {entry.priority}
        </div>
      </div>
      {resizable && (
        <span
          data-edge="end"
          onPointerDown={(ev) => {
            ev.stopPropagation();
            onPress("resize-end", ev);
          }}
          className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize bg-current/0 hover:bg-current/30"
          aria-label="Resize end"
        />
      )}
    </div>
  );
}

/** Re-resolves every 30 s, switches when the tz changes. */
function useNowInTimezone(tz: string): { weekday: number; minute: number; hhmm: string } | null {
  const [now, setNow] = useState(() => projectInTz(tz, Date.now()));
  useEffect(() => {
    setNow(projectInTz(tz, Date.now()));
    const id = window.setInterval(() => setNow(projectInTz(tz, Date.now())), 30_000);
    return () => window.clearInterval(id);
  }, [tz]);
  return useMemo(() => ({ weekday: now.weekday, minute: now.minute, hhmm: now.hhmm }), [now]);
}
